import { useRef, useState, useCallback } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';

import { useTranslation } from '../../../context/LanguageContext';
import {
  ACCEPTED_MIME_TYPES,
  IMAGE_MAX_BYTES,
  VIDEO_MAX_BYTES,
  resolveMediaType,
} from '../../../api/media.api';
import { formatBytes } from '../../../utils/format';
import type { UploadingFile } from '../../../hooks/useMediaLibrary';

interface UploadZoneProps {
  uploading: UploadingFile[];
  onFiles: (files: File[]) => void;
  onCancel: (id: string) => void;
  onDismissError: (id: string) => void;
}

export function UploadZone({
  uploading,
  onFiles,
  onCancel,
  onDismissError,
}: Readonly<UploadZoneProps>) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [typeErrors, setTypeErrors] = useState<string[]>([]);

  const validate = useCallback(
    (files: FileList | File[]): File[] => {
      const valid: File[] = [];
      const errors: string[] = [];

      for (const file of Array.from(files)) {
        const type = resolveMediaType(file.type);
        if (!type) {
          errors.push(`${file.name}: ${t('media.unsupportedType')}`);
          continue;
        }
        const limit = type === 'image' ? IMAGE_MAX_BYTES : VIDEO_MAX_BYTES;
        if (file.size > limit) {
          errors.push(`${file.name}: ${t('media.fileTooLarge')} (max ${formatBytes(limit)})`);
          continue;
        }
        valid.push(file);
      }

      setTypeErrors(errors);
      return valid;
    },
    [t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const valid = validate(e.dataTransfer.files);
      if (valid.length) onFiles(valid);
    },
    [validate, onFiles]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valid = validate(e.target.files ?? []);
      if (valid.length) onFiles(valid);
      // Reset so the same file can be re-selected after an error.
      e.target.value = '';
    },
    [validate, onFiles]
  );

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        className={`
          flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 
          cursor-pointer transition-colors select-none
          ${
            dragging
              ? 'border-violet-500 bg-violet-500/10'
              : 'border-slate-700 bg-slate-800/50 hover:border-violet-500/50 hover:bg-slate-800'
          }
        `}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-500/15">
          <Upload className="h-5 w-5 text-violet-400" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-white">{t('media.dropHint')}</p>
          <p className="text-xs text-slate-500 mt-1">{t('media.sizeHint')}</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_MIME_TYPES.join(',')}
          className="sr-only"
          onChange={handleChange}
        />
      </div>

      {/* Validation errors */}
      {typeErrors.map((err) => (
        <div
          key={err}
          className="flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300"
        >
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">{err}</span>
        </div>
      ))}

      {/* Upload queue */}
      {uploading.length > 0 && (
        <div className="space-y-2">
          {uploading.map((u) => (
            <div key={u.id} className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs font-medium text-white truncate flex-1" title={u.name}>
                  {u.error ? (
                    <span className="text-rose-400">
                      {t('media.uploadError')}: {u.name}
                    </span>
                  ) : (
                    t('media.uploading', { name: u.name })
                  )}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    u.error ? onDismissError(u.id) : onCancel(u.id);
                  }}
                  className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
                  aria-label={u.error ? t('media.dismissError') : t('media.cancelUpload')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {!u.error && (
                <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-violet-500 transition-all duration-300"
                    style={{ width: `${u.percent}%` }}
                  />
                </div>
              )}
              {!u.error && (
                <p className="text-[10px] text-slate-500 mt-1 text-right">{u.percent}%</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
